import { UpgradeStageStep } from 'api/models';
import {
  getUpgradeModeHumanReadable,
  UpgradeViewMode,
} from '../helpers/UpgradesHelpers';
import useUserAccountState from 'hooks/useUserAccountState';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import * as Sentry from '@sentry/browser';
import { Button, Dialog, IconButton, TextArea } from 'subframe/index';
import { createUpgradeAssessmentStageStepAction } from 'api/frontend';
import {
  charLimitExceededErrorMessage,
  charLimits,
} from 'constants/input-validation';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'components/FormProvider';

interface FeedbackFields {
  comment: string;
}

export function UpgradeFeedbackDialog(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  upgradeId: string;
  stageNumber: number;
  step: UpgradeStageStep;
  rowNumber: number;
  columnNumber: number;
  vote: 'up' | 'down';
  mode: UpgradeViewMode;
}) {
  const { account } = useUserAccountState();
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = AnalyticsEventLogger();

  const FeedbackSchema = Yup.object().shape({
    comment: Yup.string().max(
      charLimits.ActionDetails,
      charLimitExceededErrorMessage('Comments', charLimits.ActionDetails),
    ),
  });

  const defaultValues: FeedbackFields = {
    comment: '',
  };

  const methods = useForm<FeedbackFields>({
    resolver: yupResolver(FeedbackSchema),
    defaultValues: defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = async (data: FeedbackFields) => {
    try {
      switch (props.mode) {
        case UpgradeViewMode.Plan:
          break;
        case UpgradeViewMode.Template:
          break;
        case UpgradeViewMode.Assessment:
          await createUpgradeAssessmentStageStepAction(
            props.upgradeId,
            props.stageNumber.toFixed(),
            props.step.step_number.toFixed(),
            {
              action_type:
                props.vote === 'up'
                  ? 'provide-feedback-up'
                  : 'provide-feedback-down',
              row_number: props.rowNumber,
              column_number: props.columnNumber,
              comment: data.comment,
            },
            {
              headers: { Authorization: `Bearer ${account.token}` },
            },
          );
          break;
      }
      await props.onConfirm();
      logEvent('provide-feedback-upgrade-step', {
        templateId: props.upgradeId,
        stageNumber: props.stageNumber,
        stepNumber: props.step.step_number,
        rowNumber: props.rowNumber,
        columnNumber: props.columnNumber,
      });

      enqueueSnackbar('Successfully provided feedback on Upgrade Step', {
        variant: 'success',
        autoHideDuration: toastAutoHideDuration,
      });
      props.setOpen(false);
      reset();
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(
        somethingWentWrong.replace(
          '<action>',
          'providing feedback on this Step',
        ),
        { variant: 'error', autoHideDuration: toastAutoHideDuration },
      );
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <Dialog.Content className="flex-none h-auto w-auto">
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <div className="flex w-full min-w-[384px] max-w-[448px] flex-col items-start gap-6 px-6 py-6">
            <div className="flex w-full items-center justify-between">
              <span className="text-subheader font-subheader text-default-font">
                Thank you for your feedback
              </span>
              <IconButton
                icon="FeatherX"
                onClick={() => {
                  props.setOpen(false);
                }}
              />
            </div>
            <span className="text-body font-body text-default-font">
              Your feedback helps us improve our AI suggestions.
            </span>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-subtext-color">
                Additional Comments (Optional)
              </span>
              <TextArea
                className="h-auto w-full flex-none"
                label=""
                helpText=""
              >
                <TextArea.Input
                  placeholder="Share any additional thoughts or suggestions..."
                  {...methods.register('comment')}
                />
              </TextArea>
            </div>
            <div className="flex w-full items-end justify-end gap-4">
              <Button
                loading={isSubmitting}
                size="large"
                icon="FeatherCheck"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </div>
          </div>
        </FormProvider>
      </Dialog.Content>
    </Dialog>
  );
}
